/**
 * Career Graph (Jobs ↔ Learning) の計測スキーマ。
 *
 * 「求人SEOでPVが増えた」の先にある「学習とキャリアの接続が実際に
 * 使われたか」を、PV ではなくイベント遷移で測る。イベント名と共通
 * プロパティの型はこのファイルで閉じる (acquisition-funnel.ts と同じ方針)。
 * イベントや値を足すときは
 * docs/implementation/g2-career-graph-events-2026-09-17.md も更新する。
 *
 * user_id_or_anonymous_id はプロパティに入れない。PostHog の distinct_id
 * が全イベントに自動で付き、identify 済みなら user id、未認証なら匿名 id
 * になるので、個人の紐付けはそちらが正本。識別子を payload に重複して
 * 持たせない (jobs-analytics.ts の最小化方針と同じ)。
 */
import {
  acquisitionEnvironment,
  type AcquisitionEnvironment,
} from "./acquisition-funnel";
import {
  currentLearningDeviceType,
  type LearningDeviceType,
  type LearningUserStatus,
} from "./learning-events";
import { toJobFacetSlug, type JobFacetType } from "../jobs-facets";

/** 発火したページの系統。全イベント必須。ファネルクエリの第一フィルタ */
export type CareerGraphPage =
  | "jobs_list" // /jobs 一覧
  | "jobs_facet" // /jobs/{roles|skills|locations|conditions}/:slug
  | "job_detail" // /jobs/:jobId
  | "lesson_detail"; // /learn/courses/:course/:lesson (lesson_start_from_job)

/** ページ内のどの面がイベントを出したか。view 系は "page" */
export type CareerGraphSource =
  | "page"
  | "job_card" // 一覧・ファセットの求人カード
  | "skill_chip" // 求人詳細のスキル表示 (リンク化されたとき)
  | "filter_modal" // /jobs 絞り込みモーダルの技術スタック
  | "learning_path" // 求人詳細の「ゆめさくの学習ルート」
  | "learn_links_callout" // LearnLinksCallout 帯
  | "source_cta" // 掲載元/応募 CTA
  | "similar_jobs"; // 類似求人レール (将来の面)

/**
 * Career Graph の計測名。増やすときは PostHog の insight 側も揃える。
 *
 * `source_view_click` / `direct_apply_click` は /api/jobs/:id/outbound-click
 * が受けるサーバー側の値で PostHog には出ない (jobs-analytics.ts)。
 * `source_outbound_click` はその PostHog 側の対で、cta_mode で両者を内包する。
 */
export type CareerGraphEventName =
  | "jobs_page_view"
  | "job_view"
  | "job_card_click"
  | "skill_click"
  | "learning_content_click"
  | "lesson_start_from_job"
  | "source_outbound_click"
  | "similar_job_click";

export const CAREER_GRAPH_EVENT_NAMES: readonly CareerGraphEventName[] = [
  "jobs_page_view",
  "job_view",
  "job_card_click",
  "skill_click",
  "learning_content_click",
  "lesson_start_from_job",
  "source_outbound_click",
  "similar_job_click",
];

export type CareerGraphOutboundCtaMode = "source_view" | "direct_apply";

export interface CareerGraphEventProperties {
  /** 発火したページの系統。全イベント必須 */
  source_page: CareerGraphPage;
  /** ページ内の発火面。全イベント必須 (view 系は "page") */
  source: CareerGraphSource;
  /** 対象求人の id (GraphQL の job.id)。求人に紐づくイベントでは必須 */
  job_id?: string;
  /**
   * 企業 id。詳細では company.companyId (公開 id)、一覧カードでは
   * summary projection が持つ company.id。同一企業でも値が違いうるので、
   * 企業軸の join には company_slug を使う
   */
  company_id?: string;
  /** 企業 slug。詳細・一覧どちらでも取れる安定した join キー */
  company_slug?: string;
  /** 職種。求人では job.category、role ファセット頁ではそのラベル */
  role?: string;
  /**
   * スキル識別子。求人タグの表記ゆれを toJobFacetSlug で正規化した値。
   * /jobs/skills/:facetSlug の slug と同じ形なので facet への遷移と結べる
   */
  skill_id?: string;
  /** 遷移先。内部は path、外部は hostname (生の遷移先 URL は載せない) */
  target_page?: string;
  /** jobs_facet の系統と値。facet 頁のイベントでは両方とも必須 */
  facet_type?: JobFacetType;
  facet?: string;
  /** 学習側の行き先・発火に関わる識別子 */
  course_slug?: string;
  lesson_id?: string;
  /** similar_job_click の遷移元求人 */
  from_job_id?: string;
  /** source_outbound_click の CTA 系統 (掲載元を見る / 直接応募) */
  cta_mode?: CareerGraphOutboundCtaMode;
  /** 面固有の短い識別子 (filter では "select" / "deselect" など) */
  action?: string;
  /** 一覧上の位置 (0 始まり) */
  position?: number;
  /** その時点で表示中の求人数 */
  job_count?: number;
  device_type?: LearningDeviceType;
  environment?: AcquisitionEnvironment;
  user_status?: LearningUserStatus;
  /** 面固有の値をそのまま乗せる */
  [property: string]: string | number | boolean | undefined;
}

export type CareerGraphTrackEvent = (
  event: string,
  properties?: Record<string, unknown>,
) => void;

/**
 * Analytics is observational. A capture failure must never block rendering or
 * the navigation that the visitor explicitly requested.
 */
export function captureCareerGraphEvent(
  trackEvent: CareerGraphTrackEvent,
  event: CareerGraphEventName,
  properties: CareerGraphEventProperties,
): void {
  try {
    trackEvent(
      event,
      Object.fromEntries(
        Object.entries({
          device_type: currentLearningDeviceType(),
          environment: acquisitionEnvironment(),
          ...properties,
        }).filter((entry) => entry[1] !== undefined),
      ),
    );
  } catch {
    // Fail open: ページ描画と遷移が計測より先。
  }
}

/** 求人タグの表記ゆれを吸収する skill_id。facet URL の slug と同じ正規化 */
export function careerGraphSkillId(skill: string): string {
  return toJobFacetSlug(skill);
}

const JOB_ATTRIBUTION_KEY = "career-graph:job-attribution";

export interface CareerGraphJobAttribution {
  job_id: string;
  company_id?: string;
  company_slug?: string;
  role?: string;
  /** 求人詳細で踏まれた学習ルートのコース。照合用 */
  course_slug?: string;
}

function isAttributionRecord(
  value: unknown,
): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function optionalString(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

/**
 * 求人詳細から学習側へ進んだ記録をタブセッションに残す。
 * URL パラメータで運ぶとコース頁 → レッスン頁の遷移で落ちるので、
 * sessionStorage に置く。個人情報は入れない (求人の公開 id だけ)。
 */
export function rememberJobAttribution(
  attribution: CareerGraphJobAttribution,
  storage?: Pick<Storage, "setItem">,
): void {
  const store =
    storage ??
    (typeof window !== "undefined" ? window.sessionStorage : undefined);
  if (!store) return;
  try {
    store.setItem(JOB_ATTRIBUTION_KEY, JSON.stringify(attribution));
  } catch {
    // 私用モード等で storage が拒否されても遷移は止めない
  }
}

/**
 * レッスン頁が求人由来の属性を読む。記録した course_slug と現在のコースが
 * 一致するときだけ返す (求人から来ても別コースのレッスンは起因にしない)。
 * 読み捨てにはしない — 同じコースのレッスンを続けて始めた分も
 * lesson_start_from_job として数える。
 */
export function jobAttributionForLesson(
  courseSlug: string,
  storage?: Pick<Storage, "getItem">,
): CareerGraphJobAttribution | undefined {
  const store =
    storage ??
    (typeof window !== "undefined" ? window.sessionStorage : undefined);
  if (!store) return undefined;
  try {
    const raw = store.getItem(JOB_ATTRIBUTION_KEY);
    if (raw === null) return undefined;
    const parsed: unknown = JSON.parse(raw);
    if (!isAttributionRecord(parsed)) return undefined;
    const jobId = optionalString(parsed.job_id);
    if (!jobId) return undefined;
    const course = optionalString(parsed.course_slug);
    if (course !== undefined && course !== courseSlug) return undefined;
    return {
      job_id: jobId,
      company_id: optionalString(parsed.company_id),
      company_slug: optionalString(parsed.company_slug),
      role: optionalString(parsed.role),
      course_slug: course,
    };
  } catch {
    return undefined;
  }
}
